import { useCallback, useEffect, useState } from 'react';
import * as eventsApi from '../services/api/eventsApi';

/**
 * Loads events via services/api/eventsApi and exposes CRUD helpers.
 * Components never talk to eventsApi directly — everything goes
 * through this hook (or AppContext, which wraps it) so there is a
 * single place that knows how event data is fetched/mutated.
 */
export function useEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await eventsApi.getEvents();
      setEvents(data);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // POST → always re-fetch via GET afterwards, so the calendar dots,
  // today's schedule, and the day bottom sheet all pick up the exact
  // data Google Calendar now holds (no optimistic local append, no
  // page reload — just re-running the same GET that populated `events`
  // in the first place).
  const addEvent = useCallback(
    async (eventData) => {
      const created = await eventsApi.createEvent(eventData);
      await reload();
      return created;
    },
    [reload]
  );

  const editEvent = useCallback(
    async (id, updates) => {
      const updated = await eventsApi.updateEvent(id, updates);
      await reload();
      return updated;
    },
    [reload]
  );

  const removeEvent = useCallback(
    async (id) => {
      await eventsApi.deleteEvent(id);
      await reload();
    },
    [reload]
  );

  return { events, loading, error, reload, addEvent, editEvent, removeEvent };
}
